import {
  Dispatch,
  FormEvent,
  ForwardedRef,
  SetStateAction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { OurExpertiseData } from "../../../../../../models";
import { updateVerifierListing } from "../../../../../../service/organisation";
import { ServiceError, Status } from "../../../../../../service/Shared";
import { useAuth } from "../../../../../../useAuth";
import { areObjectsDeepEqual, useIsLoadingWrapper } from "../../../../../../utils";
import { Toast } from "../../../../../../widget";
import {
  VerifierFormNames,
  VerifierMarketingAssetsFormChangedAction,
  VerifierMarketingAssetsSubmitHandler,
} from "../../models";

interface UseOurExpertiseFormReturnData {
  enabled: boolean;
  content: string | null;
  errors: ServiceError[] | undefined;
  isHandleSubmitLoading: boolean;
  setEnabled: Dispatch<SetStateAction<boolean>>;
  setContent: Dispatch<SetStateAction<string | null>>;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  handleCancel: () => void;
  hasPermission: (permission: string) => boolean;
}

export const useOurExpertiseForm = (
  ourExpertiseDefaultValues: OurExpertiseData,
  ourExpertiseOldDefaultValues: OurExpertiseData,
  listingRowVersion: number,
  setListingRowVersion: Dispatch<SetStateAction<number>>,
  dispatch: Dispatch<VerifierMarketingAssetsFormChangedAction>,
  forwardedRef: ForwardedRef<VerifierMarketingAssetsSubmitHandler>,
  getCurrentFormsData: (formWhichWasSaved: string) => string
): UseOurExpertiseFormReturnData => {
  const { currentVerifierUuid, hasPermission } = useAuth();

  const [submittedValues, setSubmittedValues] = useState<OurExpertiseData>(ourExpertiseOldDefaultValues);
  const [errors, setErrors] = useState<ServiceError[] | undefined>();

  const [enabled, setEnabled] = useState(ourExpertiseDefaultValues.enabled);
  const [content, setContent] = useState(ourExpertiseDefaultValues.content);
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);

  useImperativeHandle(forwardedRef, () => ({
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    getOurExpertiseData() {
      return {
        enabled,
        content,
      };
    },
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    getOurExpertiseSubmittedData() {
      return {
        ...submittedValues,
      };
    },
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    setOurExpertiseErrors(previewErrors: ServiceError[]) {
      setErrors(previewErrors);
    },
  }));

  const getCurrentFormData = useCallback((): OurExpertiseData => {
    return {
      enabled,
      content: content || null,
    };
  }, [enabled, content]);

  useEffect(() => {
    dispatch({
      type: "SET_OUR_EXPERTISE_FORM_CHANGED",
      value: !areObjectsDeepEqual(submittedValues, getCurrentFormData()),
    });
  }, [enabled, content, submittedValues]);

  const handleSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);
    if (currentVerifierUuid) {
      const newOurExpertiseValues = {
        enabled,
        content,
      };

      const res = await updateVerifierListing({
        verifierUuid: currentVerifierUuid,
        content: getCurrentFormsData(VerifierFormNames.OurExpertise),
        rowVersion: listingRowVersion,
      });

      if (res.status === Status.Success && res.data) {
        Toast.success({ message: "Our expertise details changed successfully" });
        setSubmittedValues({
          ...newOurExpertiseValues,
        });
        setListingRowVersion(res.data.rowVersion);
      }

      if (res.status === Status.Error) {
        setErrors(res.errors);
      }
    }
  }, setIsHandleSubmitLoading);

  const handleCancel = (): void => {
    setEnabled(submittedValues.enabled);
    setContent(submittedValues.content);
    setErrors([]);
  };

  return {
    enabled,
    content,
    errors,
    isHandleSubmitLoading,
    setEnabled,
    setContent,
    handleSubmit,
    handleCancel,
    hasPermission,
  };
};
